// tracking_scripts/tag_manager/events.ts

import { GTM_logEvent } from './logging';
import { GTM_Product } from './product';
import { Product } from './product_list';

/**
 * Interface representing a product's details for GTM events.
 */
interface ProductDetails {
	id: string;
	category?: string;
	name?: string;
	unit_sale_price?: string;
	unit_price?: string;
	manufacturer?: string;
	wine_category?: string;
	subcategory?: string;
	grape_type_web?: string;
	country?: string;
	brand?: string;
	case_category?: string;
	case_size?: string;
	product_location?: string;
	spirits_category?: string;
	beer_style?: string;
	container?: string;
	size?: string;
	beer_case_type?: string;
	quantity?: number;
	[key: string]: any;
}

/**
 * Interface for GTM event objects sent to the data layer.
 */
interface GTMEvent {
	event: string;
	ecommerce?: {
		currency: string;
		items?: any[];
		value?: number;
		step_name?: string;
		step_number?: string;
		transaction_id?: string;
		payment_method?: string;
		tax?: string;
		shipping?: string;
		coupon?: string;
	} | null;
	click_event?: {
		category: string;
		action: string;
		label: string;
		link_url: string;
		link_text: string;
		page_url: string;
	};
}

const GTM_list = window.serverData?.GTM_list;

/**
 * Retrieves the page type from the universal_variable.
 * Used to determine how information is stored in the universal_variable.
 * @returns {string} The page type as determined by universal_variable.
 */
const pageType = (): string => {
	return (window as any).universal_variable?.page?.type || '';
};

/**
 * Product on a page has been added to basket.
 * The function call is embedded in various areas in main.js.
 * Determines whether the product is already in the productList or needs to be created.
 *
 * @param {number} id - item_store_id, unique to each product
 * @param {number} qty - The amount being added
 * @param {string|null|undefined} [product_name] - Optional name of the product (can be a string, null, or undefined).
 * @param {Array} basketContents - The total number of products as items in the product line.
 * @param {number} basketSubtotal - The basket subtotal
 * @param {number} basketTotal - The basket total
 * @example
 * // Add a product from the page to the basket
 * GTM_productFromPage(12345, 1);
 */
export function GTM_productFromPage(
	id: number,
	qty: number,
	product_name: string | null | undefined,
	basketContents: any[] | null | undefined,
	basketSubtotal: number | null | undefined,
	basketTotal: number | null | undefined,
): void {
	let storeId = id.toString();
	let currentPageType = pageType();
	let { product, index } = GTM_list.find(storeId) ?? {};

	if (product) {
		let updatedQty = parseInt(product.quantity?.toString() ?? '0') + qty;
		GTM_createEvent(product as ProductDetails, qty, 'add_to_cart');
		GTM_list.update(index as number, updatedQty);
	} else {
		let productDetails: ProductDetails = {
			id: storeId,
			category: '',
		};

		if (currentPageType === 'product') {
			productDetails = (window as any).universal_variable?.product;
		} else if (currentPageType === 'category') {
			let items = (window as any).universal_variable?.listing?.items;
			if (items) {
				items.forEach((item: ProductDetails) => {
					if (item.id === storeId) {
						productDetails = item;
					}
				});
			}
		}
		product = new GTM_Product(productDetails, qty).getProduct();

		if (product && product_name) {
			product.item_name = product_name;
		}

		GTM_createEvent(product as ProductDetails, qty, 'add_to_cart');
		GTM_list.add(product as Product);

		(window as any).GTM_processActive = false;
	}

	// Update basket universal_variable using the same product data
	if (!basketContents) return;

	const basket = window.universal_variable?.basket;
	if (!basket) return;

	if (!Array.isArray(basketContents) || basketContents.length === 0) {
		basket.line_items = [];
	} else {
		basket.line_items = basketContents.map(function (item) {
			return {
				product: {
					name: item.name,
					sku_code: item.productCode,
					price: item.price,
				},
				quantity: item.quantity,
				subtotal: Number(item.price || 0) * Number(item.quantity || 0),
			};
		});
	}

	if (!basketSubtotal || !basketTotal) return;
	basket.subtotal = basketSubtotal;
	basket.total = basketTotal;
}

/**
 * Product in the basket has been updated.
 * Update types include qty increase/decrease, deleted, add another, etc.
 *
 * @param {Array} basketResponseItems - Products returned from the basket response object.
 * @param {Array} basketContents - The basket contents from the add-to-basket response
 * @param {number} basketSubtotal - The basket subtotal number
 * @param {number} basketTotal - The basket total number
 *
 * @example
 * // Update the basket with new product quantities
 * GTM_productFromBasket(basketResponseItems);
 */
export function GTM_productFromBasket(
	basketResponseItems: any[],
	basketContents: any[],
	basketSubtotal: number,
	basketTotal: number,
): void {
	basketResponseItems.forEach((item) => {
		let storeId = item.productId.toString();
		let lineQty = parseInt(item.lineQty?.toString() ?? '0');
		let adjustQty;
		let { product, index } = GTM_list.find(storeId) ?? {};

		if (product) {
			if (lineQty > (product.quantity ?? 0)) {
				adjustQty = lineQty - (product.quantity ?? 0);
				GTM_createEvent(product as ProductDetails, adjustQty, 'add_to_cart'); // Cast to ProductDetails
			} else {
				adjustQty = (product.quantity ?? 0) - lineQty;
				GTM_createEvent(product as ProductDetails, adjustQty, 'remove_from_cart'); // Cast to ProductDetails
			}
			if (index !== undefined) {
				lineQty === 0 ? GTM_list.remove(index) : GTM_list.update(index, lineQty);
			}
		}
	});

	// Update basket universal_variable using the same product data

	const basket = window.universal_variable?.basket;
	if (!basket) return;

	if (!Array.isArray(basketContents) || basketContents.length === 0) {
		basket.line_items = [];
	} else {
		basket.line_items = basketContents.map(function (item) {
			return {
				product: {
					name: item.name,
					sku_code: item.productCode,
					price: item.price,
				},
				quantity: item.quantity,
				subtotal: Number(item.price || 0) * Number(item.quantity || 0),
			};
		});
	}

	basket.subtotal = basketSubtotal;
	basket.total = basketTotal;
}

/**
 * Creates an event object to send to Google Tag Manager.
 *
 * @param {ProductDetails} product - Details of the product triggering the event.
 * @param {number} qty - Quantity of the product.
 * @param {string} event - Type of GTM event (e.g., 'add_to_cart').
 *
 * @example
 * // Create an 'add to cart' event for a product
 * const product: ProductDetails = { id: '123', name: 'Wine Bottle', quantity: 1 };
 * GTM_createEvent(product, 1, 'add_to_cart');
 */
export function GTM_createEvent(product: ProductDetails, qty: number, event: string): void {
	const _product = { ...product, quantity: qty }; // Shallow copy to avoid modifying the original product
	const eventObj: GTMEvent = {
		event: event,
		ecommerce: {
			currency: 'GBP',
			items: [_product],
		},
	};
	GTM_sendEvent(eventObj);
}

/**
 * Creates a click event object to send to Google Tag Manager.
 *
 * @param {string} category - Custom category for the click (e.g., 'homepage').
 * @param {string} action - Custom action for the click (e.g., 'home-hero-carousel').
 * @param {string} label - Descriptive label of the element clicked (e.g., 'Explore our wine cases').
 * @param {string} value - .
 * @param {string} link_url - The URL the clicked link points to.
 * @param {string} link_text - The visible text of the clicked link.
 * @param {string} page_url - The page where the click occurred.
 *
 * @example
 * // Create a click event for a homepage button
 * GTM_createClickEvent(
 *   'homepage',
 *   'home-hero-carousel',
 *   'Explore our wine cases',
 *   'https://example.com/wine-cases',
 *   'Explore our wine cases',
 *   'https://example.com'
 * );
 */
export function GTM_createClickEvent(
	category: string | null,
	action: string | null,
	label: string | null,
	value: string | null,
	link_url: string | null,
	link_text: string | null,
	page_url: string | null,
): void {
	const eventObj = {
		event: 'click_event',
		click_event: {
			category: category || '',
			action: action || '',
			label: label || '',
			value: value || '',
			link_url: link_url || '',
			link_text: link_text || '',
			page_url: page_url || '',
		},
	};

	GTM_sendEvent(eventObj);
}

/**
 * Sends a GTM event to the data layer.
 *
 * @param {GTMEvent} eventObj - The event object to push to the GTM data layer.
 *
 * @example
 * // Send a purchase event
 * const purchaseEvent: GTMEvent = {
 *     event: 'purchase',
 *     ecommerce: {
 *         currency: 'GBP',
 *         transaction_id: 'T12345',
 *         value: 100.00,
 *         items: [{ id: '123', name: 'Wine Bottle', quantity: 1 }]
 *     }
 * };
 * GTM_sendEvent(purchaseEvent);
 */
function GTM_sendEvent(eventObj: GTMEvent): void {
	(window as any).dataLayer.push({
		ecommerce: null,
	});
	(window as any).dataLayer.push(eventObj);
	GTM_logEvent(eventObj);
}

/**
 * Creates a checkout event for Google Tag Manager (SPA-safe).
 * Pass the current step; falls back to 'loginOrRegister'.
 *
 * @param {'loginOrRegister'|'accountDetails'|'deliveryAddress'|'paymentMethod'|'review'} [step] - Current checkout step.
 * @returns {void}
 */
export function GTM_createCheckoutEvent(
	step?: 'loginOrRegister' | 'accountDetails' | 'deliveryAddress' | 'paymentMethod' | 'review',
): void {
	const checkoutStep = step || 'loginOrRegister';
	const checkoutStepNumber: Record<
		'loginOrRegister' | 'accountDetails' | 'deliveryAddress' | 'paymentMethod' | 'review',
		string
	> = {
		loginOrRegister: '0',
		accountDetails: '1',
		deliveryAddress: '2',
		paymentMethod: '3',
		review: '4',
	};

	const basketTotal = (window as any).universal_variable?.basket?.total || 0;

	const eventObj: GTMEvent = {
		event: 'checkout',
		ecommerce: {
			currency: 'GBP',
			value: basketTotal,
			step_name: checkoutStep,
			step_number: checkoutStepNumber[checkoutStep],
			items: GTM_list.findAll(),
		},
	};

	GTM_sendEvent(eventObj);
}

/**
 * Creates a purchase confirmation event for Google Tag Manager.
 * Retrieves the transaction info from the universal_variable.
 *
 * @example
 * // Create and send a purchase confirmation event
 * GTM_createConfirmationEvent();
 */
export function GTM_createConfirmationEvent(): void {
	let transactionDetails = (window as any).universal_variable?.transaction;
	if (transactionDetails) {
		const eventObj: GTMEvent = {
			event: 'purchase',
			ecommerce: {
				currency: 'GBP',
				transaction_id: transactionDetails?.order_id,
				value: transactionDetails?.total,
				payment_method: transactionDetails?.payment_type,
				tax: '',
				shipping: (
					parseFloat(transactionDetails?.total) - parseFloat(transactionDetails?.subtotal)
				).toFixed(2),
				coupon: '',
				items: GTM_list.findAll(),
			},
		};
		GTM_sendEvent(eventObj);
	}
}
